// OwnerSearchPicker — "which AppFolio owner is it?": inside the Owners page's
// Mark resolved dialog for an owner-CREATE step (owner-sync spec §7), and on an
// EPMA owner card marked "Existing owner" (§13.1, with its own label + no-results
// line and no choice line).
//
// Resolving a stuck create by hand has to name the owner AppFolio now holds,
// or the note / attachments / taxpayer ID waiting on it never run — the server
// refuses a create resolve without one (409 owner_required). So staff search
// AppFolio owners (GET /api/owners/search) and pick one; the pick goes up to the
// dialog, whose Confirm stays disabled until there is one.
//
// Names, emails and cities only: the owner id is the result row's key and the
// resolve request's `appfolioId`, never text (docs/no-ids-in-ui.md).

import { useEffect, useRef, useState } from 'react';
import { RecipientResultList } from '@/components/ownerSync/RecipientResultList';
import { ownerMatchDetail, ownerResultRow, type OwnerMatch } from '@/lib/ownerSync';
import { searchOwners } from '@/lib/ownerSyncClient';

const MIN_QUERY = 2;
const SEARCH_DEBOUNCE_MS = 300;

export function OwnerSearchPicker({
  id,
  initialQuery,
  selected,
  onPick,
  ifMissing,
  label = 'The owner in AppFolio',
  emptyText,
  showChoiceLine = true,
  disabled = false,
}: {
  /** Prefix for the input id (a dialog can be open for one step at a time). */
  id: string;
  /** Seeds the search — the step's owner name, so the likely match is already listed. */
  initialQuery?: string | undefined;
  selected: OwnerMatch | null;
  onPick: (m: OwnerMatch) => void;
  /** What to do when the owner is not in AppFolio ("use Retry", …) — from the step's flags; omitted = say nothing. */
  ifMissing?: string | null | undefined;
  /** The search field's label (the EPMA form says "Find the owner in AppFolio"). */
  label?: string;
  /** Replaces the no-results line entirely (ifMissing is then ignored). */
  emptyText?: string | undefined;
  /** The "Marking done with …" / "Choose the owner …" line — the resolve dialog's; off elsewhere. */
  showChoiceLine?: boolean;
  disabled?: boolean;
}): React.JSX.Element {
  const [query, setQuery] = useState(initialQuery ?? '');
  const [results, setResults] = useState<OwnerMatch[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Debounced; the newest query wins and a late reply is dropped.
  const seqRef = useRef(0);
  useEffect(() => {
    const q = query.trim();
    const seq = ++seqRef.current;
    if (q.length < MIN_QUERY) {
      setResults(null);
      setSearching(false);
      setSearchError(null);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      void (async () => {
        try {
          const rows = await searchOwners(q);
          if (seq === seqRef.current) {
            setResults(rows);
            setSearchError(null);
          }
        } catch {
          if (seq === seqRef.current) setSearchError('Could not search AppFolio owners just now. Try again in a moment.');
        } finally {
          if (seq === seqRef.current) setSearching(false);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const pick = (key: string) => {
    const m = results?.find((r) => r.appfolioOwnerId === key);
    if (m) onPick(m);
  };

  const selectedDetail = selected ? ownerMatchDetail(selected) : '';

  return (
    <div className="stack-sm" style={{ marginBottom: 'var(--space-4)' }}>
      <div className="field" style={{ margin: 0 }}>
        <label htmlFor={`${id}-search`}>{label}</label>
        <input
          id={`${id}-search`}
          type="search"
          className="input"
          placeholder="Name, company or email"
          value={query}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
        />
        <span className="helper">
          {searching ? 'Searching…' : `Type at least ${MIN_QUERY} characters, then choose the owner.`}
        </span>
      </div>
      {searchError && <p className="oc-meta" style={{ margin: 0 }}>{searchError}</p>}
      {!searchError && results !== null && (
        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          <RecipientResultList
            rows={results.map(ownerResultRow)}
            selectedKey={selected?.appfolioOwnerId ?? null}
            onPick={pick}
            emptyText={
              emptyText ??
              `No AppFolio owners match.${ifMissing ? ` If the owner is not in AppFolio, cancel and ${ifMissing} instead.` : ''}`
            }
          />
        </div>
      )}
      {showChoiceLine && (
        <p className="oc-meta" style={{ margin: 0 }} data-testid="resolve-owner-choice">
          {selected ? (
            <>
              Marking done with <strong>{selected.displayName}</strong>
              {selectedDetail ? ` (${selectedDetail})` : ''}.
            </>
          ) : (
            'Choose the owner before marking this step done.'
          )}
        </p>
      )}
    </div>
  );
}
